import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import { forwardRef } from 'react'
import Link from 'next/link'

const styles = {
  base: [
    // Beautiful modal-style buttons with gradients and animations
    'relative overflow-hidden inline-flex items-center justify-center gap-x-1 sm:gap-x-2 font-semibold rounded-lg sm:rounded-xl transition-all duration-300',
    'transform hover:scale-[1.01] sm:hover:scale-[1.02] hover:shadow-md sm:hover:shadow-lg active:scale-[0.99] sm:active:scale-[0.98] cursor-pointer',
    // Focus
    'focus:outline-hidden data-focus:outline data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500',
    // Disabled styles are handled in component logic
    // Shimmer effect
    'group',
    // Before pseudo-element for the shimmer animation
    'before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/0 before:via-white/20 before:to-white/0',
    'before:translate-x-[-100%] before:transition-transform before:duration-700',
    'hover:before:translate-x-[100%]',
    // Icon animations
    '*:data-[slot=icon]:transition-transform *:data-[slot=icon]:duration-200',
    '*:data-[slot=bounce]:group-hover:animate-bounce',
    '*:data-[slot=slide]:group-hover:translate-x-1',
  ],
  sizes: {
    sm: 'px-2 py-1.5 text-xs sm:px-3 sm:py-1.5 sm:text-sm',
    md: 'px-3 py-2 text-sm sm:px-4 sm:py-2 sm:text-base',
    lg: 'px-4 py-3 text-base sm:px-6 sm:py-3 sm:text-lg',
  },
  outline: [
    // Outline variant with beautiful styling
    'border-2 bg-transparent hover:shadow-md',
  ],
  plain: [
    // Plain variant for minimal styling
    'border-transparent hover:shadow-sm bg-transparent',
  ],
  colors: {
    // Exact modal color combinations
    green: [
      'bg-gradient-to-r from-[#16a34a] to-[#059669] hover:from-[#15803d] hover:to-[#047857] text-white',
    ],
    blue: [
      'bg-gradient-to-r from-[#2563eb] to-[#4f46e5] hover:from-[#1d4ed8] hover:to-[#4338ca] text-white',
    ],
    amber: [
      'bg-gradient-to-r from-[#d97706] to-[#ea580c] hover:from-[#b45309] hover:to-[#c2410c] text-white',
    ],
    red: [
      'bg-gradient-to-r from-[#dc2626] to-[#e11d48] hover:from-[#b91c1c] hover:to-[#be123c] text-white',
    ],
    gray: ['bg-[#f3f4f6] hover:bg-[#e5e7eb] text-[#374151] font-medium'],
    dark: ['bg-black hover:bg-[#111827] text-white'],
    purple: [
      'bg-gradient-to-r from-[#9333ea] to-[#7c3aed] hover:from-[#7e22ce] hover:to-[#6d28d9] text-white',
    ],
    indigo: [
      'bg-gradient-to-r from-[#4f46e5] to-[#2563eb] hover:from-[#4338ca] hover:to-[#1d4ed8] text-white',
    ],
    emerald: [
      'bg-gradient-to-r from-[#059669] to-[#16a34a] hover:from-[#047857] hover:to-[#15803d] text-white',
    ],
    teal: [
      'bg-gradient-to-r from-[#0d9488] to-[#0891b2] hover:from-[#0f766e] hover:to-[#0e7490] text-white',
    ],
    orange: [
      'bg-gradient-to-r from-[#ea580c] to-[#dc2626] hover:from-[#c2410c] hover:to-[#b91c1c] text-white',
    ],
    pink: [
      'bg-gradient-to-r from-[#db2777] to-[#e11d48] hover:from-[#be185d] hover:to-[#be123c] text-white',
    ],
  },
  outlineColors: {
    green: [
      'border-[#16a34a] text-[#16a34a] hover:bg-gradient-to-br hover:from-[#f0fdf4] hover:to-[#ecfdf5]',
    ],
    blue: [
      'border-[#2563eb] text-[#2563eb] hover:bg-gradient-to-br hover:from-[#eff6ff] hover:to-[#eef2ff]',
    ],
    amber: [
      'border-[#d97706] text-[#d97706] hover:bg-gradient-to-br hover:from-[#fffbeb] hover:to-[#fff7ed]',
    ],
    red: [
      'border-[#dc2626] text-[#dc2626] hover:bg-gradient-to-br hover:from-[#fef2f2] hover:to-[#fff1f2]',
    ],
    gray: ['border-[#9ca3af] text-[#4b5563] hover:bg-[#f9fafb]'],
    dark: ['border-[#1f2937] text-[#1f2937] hover:bg-[#f9fafb]'],
    purple: [
      'border-[#9333ea] text-[#9333ea] hover:bg-gradient-to-br hover:from-[#faf5ff] hover:to-[#f5f3ff]',
    ],
    indigo: [
      'border-[#4f46e5] text-[#4f46e5] hover:bg-gradient-to-br hover:from-[#eef2ff] hover:to-[#eff6ff]',
    ],
    emerald: [
      'border-[#059669] text-[#059669] hover:bg-gradient-to-br hover:from-[#ecfdf5] hover:to-[#f0fdf4]',
    ],
    teal: [
      'border-[#0d9488] text-[#0d9488] hover:bg-gradient-to-br hover:from-[#f0fdfa] hover:to-[#ecfeff]',
    ],
    orange: [
      'border-[#ea580c] text-[#ea580c] hover:bg-gradient-to-br hover:from-[#fff7ed] hover:to-[#fef2f2]',
    ],
    pink: [
      'border-[#db2777] text-[#db2777] hover:bg-gradient-to-br hover:from-[#fdf2f8] hover:to-[#fff1f2]',
    ],
  },
  plainColors: {
    green: [
      'text-[#16a34a] hover:bg-gradient-to-br hover:from-[#f0fdf4] hover:to-[#ecfdf5]',
    ],
    blue: [
      'text-[#2563eb] hover:bg-gradient-to-br hover:from-[#eff6ff] hover:to-[#eef2ff]',
    ],
    amber: [
      'text-[#d97706] hover:bg-gradient-to-br hover:from-[#fffbeb] hover:to-[#fff7ed]',
    ],
    red: [
      'text-[#dc2626] hover:bg-gradient-to-br hover:from-[#fef2f2] hover:to-[#fff1f2]',
    ],
    gray: ['text-[#4b5563] hover:bg-[#f3f4f6]'],
    dark: ['text-[#1f2937] hover:bg-[#f3f4f6]'],
    purple: [
      'text-[#9333ea] hover:bg-gradient-to-br hover:from-[#faf5ff] hover:to-[#f5f3ff]',
    ],
    indigo: [
      'text-[#4f46e5] hover:bg-gradient-to-br hover:from-[#eef2ff] hover:to-[#eff6ff]',
    ],
    emerald: [
      'text-[#059669] hover:bg-gradient-to-br hover:from-[#ecfdf5] hover:to-[#f0fdf4]',
    ],
    teal: [
      'text-[#0d9488] hover:bg-gradient-to-br hover:from-[#f0fdfa] hover:to-[#ecfeff]',
    ],
    orange: [
      'text-[#ea580c] hover:bg-gradient-to-br hover:from-[#fff7ed] hover:to-[#fef2f2]',
    ],
    pink: [
      'text-[#db2777] hover:bg-gradient-to-br hover:from-[#fdf2f8] hover:to-[#fff1f2]',
    ],
  },
}

type ButtonProps = {
  size?: keyof typeof styles.sizes
  color?: keyof typeof styles.colors
  outline?: true
  plain?: true
  className?: string
  children: React.ReactNode
  disabled?: boolean
  prefetch?: boolean
} & (
  | Omit<Headless.ButtonProps, 'as' | 'className' | 'disabled'>
  | Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>
)

export const Button = forwardRef(function Button(
  {
    size = 'md',
    color = 'green',
    outline,
    plain,
    className,
    children,
    disabled,
    prefetch = false,
    ...props
  }: ButtonProps,
  ref: React.ForwardedRef<HTMLElement>
) {
  const classes = clsx(
    className,
    styles.base,
    styles.sizes[size],
    outline
      ? [styles.outline, styles.outlineColors[color]]
      : plain
        ? [styles.plain, styles.plainColors[color]]
        : styles.colors[color],
    // Apply disabled styles when disabled
    disabled && [
      '!bg-[#9ca3af] !from-[#9ca3af] !to-[#6b7280]',
      'opacity-60 cursor-not-allowed pointer-events-none',
      'hover:!from-[#9ca3af] hover:!to-[#6b7280] hover:!scale-100 hover:!shadow-none',
      'before:!translate-x-[-100%] hover:before:!translate-x-[-100%]',
    ]
  )

  return 'href' in props ? (
    <Link
      {...props}
      prefetch={prefetch}
      className={classes}
      ref={ref as React.ForwardedRef<HTMLAnchorElement>}
    >
      <TouchTarget>{children}</TouchTarget>
    </Link>
  ) : (
    <Headless.Button
      {...props}
      disabled={disabled}
      data-disabled={disabled}
      className={clsx(classes, 'cursor-pointer')}
      ref={ref}
    >
      <TouchTarget>{children}</TouchTarget>
    </Headless.Button>
  )
})

/**
 * Expand the hit area to at least 44×44px on touch devices
 */
export function TouchTarget({ children }: { children: React.ReactNode }) {
  return (
    <>
      <span
        className="absolute left-1/2 top-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  )
}
